import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PublicEvent } from "@classview/shared-types";

import { useRegisterEventInterest } from "../api/register-event-interest";

type RegisterEventInterestModalProps = {
  event: PublicEvent;
};

export function RegisterEventInterestModal({
  event,
}: RegisterEventInterestModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    mutateAsync: registerEventInterest,
    isPending,
    isSuccess,
  } = useRegisterEventInterest(event.session?.hostOrganisationId, event.id);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleRegisterInterest = async () => {
    try {
      await registerEventInterest();
      setIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Error registering interest");
    }
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-foreground">
          Thank you for registering your interest!
        </span>
        <p className="text-sm text-muted">
          We will notify the event organiser that you are interested in
          attending this event.
        </p>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Register interest</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Register interest in {event.session.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>
            This button will notify the event organiser that you are interested
            in attending this event.
          </DialogDescription>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRegisterInterest}
            isLoading={isPending}
            isAnimated={true}
          >
            Register interest
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
